import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  viewChild
} from '@angular/core';
import {
  MotifAccordionModule,
  MotifButtonModule,
  MotifCardModule,
  MotifChipModule,
  MotifIconModule,
  MotifModal,
  MotifModalModule,
  MotifTableModule,
} from '@ey-xd/ng-motif';
import { LoggerService } from '@sa/app/core/services/logger.service';
import { ConfirmDialogComponent } from '@sa/app/shared/components/confirm-dialog/confirm-dialog.component';
import { DeliverablesConstants } from '@sa/app/shared/constants/ApplicationConstants';
import { AgentService } from '@sa/app/shared/services/agent-service';
import { UserAgentService } from '@sa/app/shared/services/user-agent.service';
import { CommonService } from '@sa/app/shared/services/common.service';
import { WorkspaceTemplateService } from '@sa/app/shared/services/workspace-template.service';
import { WorkspaceService } from '@sa/app/shared/services/workspace.service';
import { finalize, Subject, takeUntil } from 'rxjs';
import {
  ClientWorkspaceTemplateResponse,
  WorkspaceTemplateItemResponse,
  WorkspaceTemplateResponse,
} from '../../../shared/interfaces/ClientWorkspaceTemplateResponse';
import { AssignedToComponent } from '../../deliverables/assigned-to/assigned-to.component';
import { ExpandMenuComponent } from '../../deliverables/expand-menu/expand-menu.component';
import { TemplateContextMenuComponent } from '../template-context-menu/template-context-menu.component';
import { AddDeliverableTemplatePanelComponent } from './add-deliverable-template-panel/add-deliverable-template-panel.component';
import {
  MotifHardwareIcKeyboardArrowDown24px,
  MotifHardwareIcKeyboardArrowUp24px,
} from '@ey-xd/motif-icon';
// ...existing interfaces...

@Component({
  selector: 'app-deliverable-template',
  standalone: true,
  imports: [
    CommonModule,
    MotifCardModule,
    MotifButtonModule,
    MotifModalModule,
    MotifChipModule,
    MotifAccordionModule,
    MotifIconModule,
    MotifTableModule,
    ConfirmDialogComponent,
  ],
  templateUrl: './deliverable-template.component.html',
  styleUrls: ['./deliverable-template.component.scss'],
})
export class DeliverableTemplateComponent implements OnInit {
  // New properties for API structure
  workspaceTemplateData: ClientWorkspaceTemplateResponse | null = null;
  workspaceTemplates: WorkspaceTemplateResponse[] = [];
  deliverableTypes: any[] = [];
  selectedDeliverableType: any = null;
  isEYPreparer: boolean = false;
  private readonly workspaceTemplateService = inject(WorkspaceTemplateService);
  public columnHeaders: any = [];
  // public rowData: any = [];
  // private expandedMilestones: Set<any> = new Set();
  private TableHeaders = DeliverablesConstants.TableHeaders;
  //private originalData: any[] = [];
  private readonly loggerService: LoggerService = inject(LoggerService);
  protected readonly arrowCollapseIcon = MotifHardwareIcKeyboardArrowDown24px;
  protected readonly arrowExpandIcon = MotifHardwareIcKeyboardArrowUp24px;

  isMilestone: boolean = false;
  itemToDelete: string = '';
  showDeleteConfirm: boolean = false;
  readonly confirmDialog =
  viewChild.required<ConfirmDialogComponent>('confirmDialog');
  gridOptions = {
    rowHeight: 44,
    context: {
      componentParent: this,
    },
  };
  // UI state
  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';

  myTasksConstants = {
    DeliverablesConstant: 'Deliverables',
    AssignedToConstant: 'Assigned To',
  };

  private readonly commonService: CommonService = inject(CommonService);
  private readonly workspaceService: WorkspaceService =
    inject(WorkspaceService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly agentService = inject(AgentService);
  private readonly userAgentService = inject(UserAgentService);
  // Subscriptions management
  private destroy$ = new Subject<void>();

  // Track which templates are expanded
  private expandedTemplateGuids = new Set<string>();
  private agentCategoryMap = new Map<string, string>();
  constructor(private modal: MotifModal) {}

  ngOnInit(): void {
    this.gridOptions.context.componentParent = this;
    this.loadAgentCategories();
    this.setColumnHeaders();
    this.loadDeliverableTemplates();
    this.subscribeToRefreshTrigger();
    this.subscribeToClientChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAgentCategories(): void {
    this.userAgentService.getAgents().subscribe(agents => {
      agents.forEach(a => this.agentCategoryMap.set(a.applicationAgentGuid, a.agentCategory));
    });
  }

  private subscribeToRefreshTrigger(): void {
    this.agentService.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.refreshData();
    });
  }

  private subscribeToClientChanges(): void {
    this.commonService.refreshData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshData();
      });
  }

  /**
   * Load deliverable templates from backend
   */
  private loadDeliverableTemplates(): void {
    const tenantGuid = this.commonService.getState('selectedTenantGuid');
    if (!tenantGuid) {
      console.warn('No tenant GUID available');
      return;
    }

    this.isLoading = true;
    this.hasError = false;

    this.workspaceService
      .loadWorkspaceTemplates(tenantGuid)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.workspaceTemplateService.setLoading(false);
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response && response.workspaceTemplates) {
            this.workspaceTemplateData =
              response as ClientWorkspaceTemplateResponse;
            this.workspaceTemplates = response.workspaceTemplates || [];
            this.workspaceTemplateService.setWorkspaceTemplateData(
              this.workspaceTemplateData
            );
            this.cdr.detectChanges();
          } else {
            console.warn('No workspace templates returned');
          }
        },
        error: (error: any) => {
          console.error('Error loading deliverable templates:', error);
          this.hasError = true;
          this.errorMessage = 'Failed to load deliverable templates';
          this.cdr.detectChanges();
        },
      });
  }

  selectDeliverableType(deliverableType: any): void {
    this.selectedDeliverableType = deliverableType;
  }

  /**
   * Get template counts (milestones and tasks)
   */
  getTemplateCounts(template: WorkspaceTemplateResponse): {
    milestones: number;
    tasks: number;
  } {
    const milestones = template.workspaceTemplateItems?.length || 0;
    const tasks =
      template.workspaceTemplateItems?.reduce((sum, item) => {
        return sum + (item.childWorkspaceTemplateItems?.length || 0);
      }, 0) || 0;

    return { milestones, tasks };
  }

  /**
   * Restore expanded state after data refresh
   */
  private restoreExpandedState(): void {
    this.workspaceTemplates.forEach(template => {
      if (this.expandedTemplateGuids.has(template.workspaceTemplateGuid)) {
        template.expanded = true;
        this.loadWorkspaceTemplateItemGridData(template);
      }
    });
  }

  toggleDeliverable(template: WorkspaceTemplateResponse): void {
    template.expanded = !template.expanded;

    // Track expanded state
    if (template.expanded) {
      this.expandedTemplateGuids.add(template.workspaceTemplateGuid);
      // Load grid data when expanding (if not already loaded)
      if (!template.rowData || template.rowData.length === 0) {
        this.loadWorkspaceTemplateItemGridData(template);
      }
    } else {
      this.expandedTemplateGuids.delete(template.workspaceTemplateGuid);
    }
  }

  /**
   * Check if no data available
   */
  noData(): boolean {
    return !this.workspaceTemplates || this.workspaceTemplates.length === 0;
  }

  /**
   * Refresh data from backend
   */
  refreshData(): void {
    this.loadDeliverableTemplates();
  }

  // TrackBy functions for performance
  trackByTemplateGuid(
    index: number,
    template: WorkspaceTemplateResponse
  ): string {
    return template.workspaceTemplateGuid;
  }

  trackByWorkspaceTemplateItem(
    index: number,
    item: WorkspaceTemplateItemResponse
  ): string {
    return item.workspaceTemplateItemGuid;
  }

  addDeliverable(): void {
    const modalRef = this.modal.open(AddDeliverableTemplatePanelComponent, {
      slideout: true,
      data: {},
    });
    modalRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.refreshData();
      });
  }

  openModal(): void {
    this.addDeliverable();
  }

  setColumnHeaders() {
    this.columnHeaders = [
      {
        headerName: '+',
        field: 'add',
        width: 45,
        minWidth: 45,
        maxWidth: 45,
        pinned: 'left',
        resizable: false,
        cellRenderer: TemplateContextMenuComponent,
        headerComponent: TemplateContextMenuComponent,
        sortable: false,
        cellClass: 'type-cell td-left-align-bold sa-db-mt-scell-1',
        headerClass: 'add-header-font sa-db-mt-scell-1',
      },
      {
        headerName: '',
        field: 'expand',
        width: 45,
        minWidth: 45,
        maxWidth: 45,
        headerClass: 'sa-db-mt-scell-2',
        cellRenderer: ExpandMenuComponent,
        cellRendererParams: (params: any) => {
          // Get the template from grid context
          const template = params.context?.currentTemplate;
          // Only show expand if there are children and children.length > 0
          return {
            isExpanded: template?.expandedMilestones?.has(params.data.guidId) || false,
            hasChildren:
              Array.isArray(params.data.children) &&
              params.data.children.length > 0,
          };
        },
        onCellClicked: (params: any) => {
          if (
            params.data &&
            Array.isArray(params.data.children) &&
            params.data.children.length > 0
          ) {
            // Get the template from grid context
            const template = params.context?.currentTemplate;
            if (template) {
              this.toggleChildren(params.data, template);
              
            }
          }
        },
        cellClass: (params: any) =>
          params.data._isChild
            ? 'child-row milestone-header right-align sa-db-mt-scell-2'
            : 'milestone-header td-right-align sa-db-mt-scell-2',
        sortable: false,
      },
      {
        headerName: 'Type',
        field: 'type',
        sortable: false,
        flex: 1,
        minWidth: 100,
        maxWidth: 200,
        cellClass: 'type-cell td-left-align',
        headerClass: 'left-header',
        cellClassRules: {
          'milestone-header': (params: any) =>
            params.value === this.TableHeaders.Milestone ||
            params.value === this.TableHeaders.Milestones,
          'task-header': (params: any) =>
            params.value === this.TableHeaders.Task ||
            params.value === this.TableHeaders.Tasks,
        },
      },
      {
        headerName: this.TableHeaders.Processes.Name,
        field: this.TableHeaders.Processes.field,
        sortable: false,
        flex: 2,
        minWidth: 150,
        cellClass: (params: any) => {
          // Keep left-cell for alignment; indentation only for tasks
          let classes = 'left-cell';
          if (
            params.data &&
            (params.data.type === this.TableHeaders.Task ||
              params.data.type === this.TableHeaders.Tasks)
          ) {
            classes += ' processes-task-indent';
          }
          return classes;
        },
        cellRenderer: (params: any) => {
          const isMilestone =
            params.data &&
            (params.data.type === this.TableHeaders.Milestone ||
              params.data.type === this.TableHeaders.Milestones);
          const isTask =
            params.data &&
            (params.data.type === this.TableHeaders.Task ||
              params.data.type === this.TableHeaders.Tasks);
          const iconHtml = isMilestone
            ? `<svg width="15" height="15" class="pe-1" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_6016_371242)"><path d="M1.25 10.625L7.5 13.75L13.75 10.625M1.25 7.5L7.5 10.625L13.75 7.5M7.5 1.25L1.25 4.375L7.5 7.5L13.75 4.375L7.5 1.25Z" stroke="#4EBEEB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_6016_371242"><rect width="15" height="15" fill="white"/></clipPath></defs></svg>`
            : '';
          const displayValue = params.value ?? '';
          const textClass = isMilestone
            ? 'milestone-text'
            : 'motif-caption-default-light';
          return `${iconHtml}<span class="${textClass}">${displayValue}</span>`;
        },
        headerClass: 'left-header',
      },
      {
        headerName: this.TableHeaders.AssignedTo.Name,
        field: this.TableHeaders.AssignedTo.field,
        sortable: false,
        flex: 1,
        minWidth: 120,
        cellClass: 'left-cell type-cell td-left-align cell-pl4',
        headerClass: 'left-header',
        cellRenderer: AssignedToComponent,
      },
    ];
  }

  /**
   * Gets the CSS class for a row based on its type.
   * @param params - The parameters object containing data for the row.
   * @returns A string representing the CSS class for the row based on its type.
   */
  public getRowClass = (params: any) => {
    if (params.data.type === this.TableHeaders.Tasks) {
      return 'tasks-row';
    } else if (params.data.type === this.TableHeaders.Milestones) {
      return 'milestones-row';
    }

    return '';
  };

    getGridOptions(template: WorkspaceTemplateResponse) {
    return {
      rowHeight: 44,
      context: {
        componentParent: this,
        currentTemplate: template,
      },
    };
  }

  // #region Private Methods

  /**
   * Flattens hierarchical data into a single-level array for table display.
   *
   * For each top-level row, the method adds the row to the result array. If the row is expanded (present in the `expanded` set),
   * all its children are also added immediately after the parent, each marked with `_isChild: true` and a reference to the parent via `_parent`.
   *
   * @param data - The hierarchical array of rows (each may have a `children` array).
   * @param expanded - A set containing rows that are currently expanded.
   * @returns A flat array of rows suitable for table rendering, with expanded children included after their parent.
   */
  private flattenData(data: any[], expanded: Set<any>): any[] {
    let result: any[] = [];
    for (const row of data) {
      result.push(row);
      if (row.children && expanded.has(row.guidId)) {
        for (const child of row.children) {
          // Mark child as a child row for styling/indentation
          result.push({ ...child, _isChild: true, _parent: row });
        }
      }
    }

    return result;
  }

 /**
 * Toggles the expanded/collapsed state of a row and updates the grid data.
 * @param row The row object to expand or collapse.
 */
private toggleChildren(row: any, template: WorkspaceTemplateResponse): void {
  if (!template.expandedMilestones) {
    template.expandedMilestones = new Set<string>();
  }
  const rowId = row.guidId;
  if (template.expandedMilestones.has(rowId)) {
    template.expandedMilestones.delete(rowId);
  } else {
    template.expandedMilestones.add(rowId);
  }
  template.rowData = this.flattenData(template.originalData || [], template.expandedMilestones);
  if (template.gridApi) {
    template.gridApi.setGridOption('rowData', template.rowData);
    template.gridApi.refreshCells({ force: true });
  }
  this.cdr.detectChanges();
}
  /**
   * Determines if a row matches the search value in any relevant field.
   *
   * Checks if the search string (case-insensitive) is present in any of the following fields of the row:
   * - type
   * - deliverables
   * - assignedTo
   * - status
   *
   * @param row - The row object to check.
   * @param searchValue - The lowercased search string to match against.
   * @returns True if any field contains the search value; otherwise, false.
   */
  private rowMatches(row: any, searchValue: string): boolean {
    // Check all relevant fields for a match
    return (
      (row.type && row.type.toLowerCase().includes(searchValue)) ||
      (row.deliverables &&
        row.deliverables.toLowerCase().includes(searchValue)) ||
      (row.assignedTo && row.assignedTo.toLowerCase().includes(searchValue)) ||
      (row.status && row.status.toLowerCase().includes(searchValue))
    );
  }

  /**
   * Handle grid ready and set context with current template
   */
  public handleGridReady(event: any, template: WorkspaceTemplateResponse): void {
  const api = event.api;

  // Store current template in grid context
  if (api?.gridOptionsWrapper?.gridOptions?.context) {
    api.gridOptionsWrapper.gridOptions.context.componentParent = this;
    api.gridOptionsWrapper.gridOptions.context.currentTemplate = template;
  }

  // Load data if not already loaded
  if (!template.rowData || template.rowData.length === 0) {
    this.loadWorkspaceTemplateItemGridData(template);
  }

  // Size columns to fit
  if (api) {
    api.sizeColumnsToFit();
  }
}

  /**
   * Loads workspace template item grid data from the API and processes it for display.
   * @param template
   * @returns
   */
  loadWorkspaceTemplateItemGridData(template: any) {
    const tenantGuid = this.commonService.getState('selectedTenantGuid');
    const workspaceTemplateGuid = template.workspaceTemplateGuid;
    this.workspaceTemplateService.setSelectedTemplate(template);

    if (!tenantGuid) {
      console.warn('No tenant GUID available');
      return;
    }

    // Initialize template-specific properties
    template.rowData = [];
    template.originalData = [];
    template.expandedMilestones = new Set();

    this.workspaceService
      .loadWorkspaceTemplateItem(tenantGuid, workspaceTemplateGuid)
      .subscribe((apiResponse: any) => {
        //this.workspaceDetailsInformation.set(apiResponse.data);
        const mapNode = (node: any, parentGuid: string | null = null) => ({
          type: node.data.type,
          deliverables: node.data.title,
          taskGuidId: node.data.id,
          assignedToGuidId: node.data.assignedToGuidId
            ? node.data.assignedToGuidId
            : node.data.assignedToAgentGuid
              ? node.data.assignedToAgentGuid
              : '',
          //workspaceTemplateGuid: this.workspaceTemplateGuid,
          assignedTo: node.data.assignedToGuidId
            ? node.data.assignedTo
            : node.data.assignedToAgentGuid
              ? node.data.assignedToAgent
              : '',
          assignedToType: node.data.assignedToGuidId
            ? 'user'
            : node.data.assignedToAgentGuid
              ? 'agent'
              : '',
          agentCategory: node.data.assignedToAgentGuid
            ? (this.agentCategoryMap.get(node.data.assignedToAgentGuid) ?? '')
            : '',
          taskDuration: '',
          guidId: node.data.id,
          description: node.data.description,
          sortOrder: node.data.sortOrder,
          parentGuid: parentGuid,

          children: (node.children || []).map((child: any) =>
            mapNode(child, node.data.id)
          ),
        });
        template.originalData = (apiResponse.children || []).map((node: any) =>
          mapNode(node, apiResponse.data.id)
        );

        // Initialize expanded milestones set for this template
        if (!template.expandedMilestones) {
          template.expandedMilestones = new Set();
        }
        // // Auto-expand all milestones
        // template.originalData.forEach((ms: any) => {
        //   template.expandedMilestones.add(ms);
        // });
        const validIds = new Set(template.originalData.map((m: any) => m.guidId));
        template.expandedMilestones.forEach((id: any) => {
          if (!validIds.has(id)) {
        template.expandedMilestones.delete(id);
      }
    });
        // Flatten and store row data specifically for THIS template
        template.rowData = this.flattenData(
          template.originalData,
          template.expandedMilestones
        );
        this.cdr.detectChanges();
      });
  }

  public onDeleteTask(itemToDelete: any) {
    this.isMilestone = false;
    this.showDeleteConfirm = true;
    this.itemToDelete = itemToDelete.workspaceTemplateItemGuid;
    if (itemToDelete.deleteTask) {
      const item: any = {
        header: 'Confirm Task Deletion',
        message: 'Are you sure you want to delete this task?',
      };
      this.confirmDialog().confirm(item);
    }
  }

  public onDeleteMilestone(item: any) {
    this.isMilestone = true;
    this.showDeleteConfirm = true;
    this.itemToDelete = item.workspaceTemplateItemGuid;
    if (item.deleteMilestone) {
      const item: any = {
        header: 'Confirm Milestone Deletion',
        message:
          'Deleting Milestone will also delete the Child Tasks. Do you want to Proceed?',
      };
      this.confirmDialog().confirm(item);
    }
  }

  handleConfirmDelete() {
    this.onDeleteConfirmed();
  }

  handleRejectDelete() {
    this.loggerService.logInfo('Delete cancelled.');
  }

  public onDeleteConfirmed() {
    this.showDeleteConfirm = false;
    const tenantGuid =
      this.commonService.getState('selectedTenantGuid') ||
      JSON.parse(sessionStorage.getItem('TenantId') || '""');
    this.workspaceService
      .deleteWorkspaceTemplateItem(tenantGuid, this.itemToDelete)
      .subscribe({
        next: response => {
          if (response) {
            if (this.isMilestone) {
              this.loggerService.logInfo('Milestone deleted successfully.');
            } else {
              this.loggerService.logInfo('Task deleted successfully.');
            }
            this.loadDeliverableTemplates();
          }
        },
        error: err => {
          this.loggerService.logApiError(err?.error?.message);
        },
      });
  }
}
